import md5 from 'md5'

import { Ga4Item , queueAnalyticsEvent, SeoEvent } from 'lib/gtm'
import { CustomerTypes, isCustomerAdult } from 'modules/customer'
import {
  formatPriceWithoutCurrency,
  getProductCategoriesForSeo,
  getProductUnitDetails,
  isNewProduct,
  ProductTypes
} from 'modules/products'
import {
  logAnalyticsEvents,
  ProductSeoStorage,
  SeoLastSelectedProductDataStorage,
  SeoListName,
  SeoPromotion} from 'shared/analytics'
import { getNumberOfDayFromDate } from 'shared/utils/date'

import { Cart, CartItem } from './types'

export const collectItemsByCart = (products: CartItem[]): Ga4Item[] => {
  const productsInLocalstorages = ProductSeoStorage.get()

  const cartProductsFoundInLocalStorages = new Array<Ga4Item>(
    productsInLocalstorages.length
  )

  const cartProductsNotFoundInLocalStorages: Ga4Item[] = []

  products.forEach((product, idx) => {
    const specificProduct: ProductTypes = product.product

    const indexInLocalStorage = productsInLocalstorages.findIndex(
      item => item.productId === specificProduct.id
    )

    const productInLocalStorage = productsInLocalstorages[indexInLocalStorage]

    const item: Ga4Item = {
      product_id: specificProduct.id,
      item_name: specificProduct?.nameLong || undefined,
      item_id: specificProduct?.code || undefined,
      price: formatPriceWithoutCurrency(
        specificProduct?.specialOfferPricing?.priceGross?.value ||
          specificProduct?.pricing?.priceGross?.value ||
          0
      ),
      discount: specificProduct?.specialOfferPricing
        ? formatPriceWithoutCurrency(
            specificProduct?.pricing?.priceGross?.value ||
              0 - specificProduct?.specialOfferPricing?.priceGross?.value ||
              0
          )
        : 0,
      currency:
        specificProduct?.pricing?.priceGross?.symbol === 'zł'
          ? 'PLN'
          : undefined,
      item_brand: specificProduct?.brand?.name || undefined,
      item_promotion: specificProduct?.specialOfferPricing ? 'yes' : 'no',
      item_available: specificProduct?.isAvailable ? 'yes' : 'no',
      item_new: isNewProduct(specificProduct?.tags || []) ? 'yes' : 'no',
      item_adult_only:
        specificProduct?.ageLimit && specificProduct?.ageLimit >= 18
          ? 'yes'
          : 'no',
      index: idx,
      ...getProductCategoriesForSeo(specificProduct),
      item_variant: getProductUnitDetails(specificProduct) || undefined,
      item_list_name: productInLocalStorage?.listName,
      quantity: product.quantity,
      promotion_id: productInLocalStorage?.promotion?.promotionId || undefined,
      creative_name: productInLocalStorage?.promotion?.creativeName || undefined
    }

    if (productInLocalStorage) {
      cartProductsFoundInLocalStorages[indexInLocalStorage] = item
    } else {
      cartProductsNotFoundInLocalStorages.push(item)
    }
  })

  return [
    ...cartProductsNotFoundInLocalStorages,
    //clear undefined items
    ...cartProductsFoundInLocalStorages.filter(item => !!item)
  ]
}

interface ViewCartListProps {
  user: CustomerTypes | undefined
  products: CartItem[]
  listName: SeoListName
}

export const viewCartList = ({
  user,
  products,
  listName
}: ViewCartListProps) => {
  queueAnalyticsEvent({ ecommerce: null })

  const items = collectItemsByCart(products)

  const eventData = {
    event: SeoEvent.VIEW_ITEM_LIST, //constant
    type: 'ecommerce', //constant
    user_id: user?.code || undefined, //User ID from crm if logged in, if not we return undefined
    user_adult: isCustomerAdult(user?.dateOfBirth), //1 if user have more than 18 years, 0 - if no, if no value we return undefined
    hashed_email: user?.email ? md5(user?.email) : undefined, //customer email in md5 - if logged in, if not we return undefined
    ecommerce: {
      listName,
      items
    }
  }

  queueAnalyticsEvent(eventData)
}

interface addToCartProps {
  user: CustomerTypes | undefined
  product: ProductTypes
  button_place: string
  idxOnList: number
  productQuantity: number
  cart: Cart | null
  listName: SeoListName
  type: 'add' | 'increment'
}

export const addToCartSeo = ({
  user,
  product,
  button_place,
  idxOnList,
  productQuantity,
  cart,
  listName,
  type
}: addToCartProps) => {
  if (type !== 'add') {
    return
  }

  const id = product.id
  const storedItem = ProductSeoStorage.find(id) || undefined
  const coupon = cart?.discountCodes[0]?.code || undefined
  const isLastProductSelected = SeoLastSelectedProductDataStorage.get()
  const matchedListName =
    storedItem?.listName || isLastProductSelected?.listName || listName

  if (!storedItem) {
    ProductSeoStorage.add({
      productId: id,
      listName: matchedListName
    })
  }
  queueAnalyticsEvent({ ecommerce: null })

  const promotion = SeoPromotion.get()

  const eventData = {
    event: SeoEvent.ADD_TO_CART, //constant
    type: 'ecommerce', //constant
    user_id: user?.code || undefined, //User ID from crm if logged in, if not we return undefined
    user_adult: isCustomerAdult(user?.dateOfBirth), //1 if user have more than 18 years, 0 - if no, if no value we return undefined
    hashed_email: user?.email ? md5(user?.email) : undefined, //customer email in md5 - if logged in, if not we return undefined
    button_place, //button place where product was added to cart. It can be like: cart page, product page, product page - listing, category page, profile page, etc.
    ecommerce: {
      value: Number(
        formatPriceWithoutCurrency(
          product?.specialOfferPricing?.priceGross?.value ||
            product?.pricing?.priceGross?.value ||
            0
        ) * (productQuantity || 1)
      ).toFixed(2),
      currency:
        product?.pricing?.priceGross?.symbol === 'zł' ? 'PLN' : undefined,
      coupon, //coupon if exists - if not undefined
      items: [
        {
          item_name: product.nameLong || undefined, //product name
          item_id: product.code || undefined, // product id - it should be the same as you have in google merchant center and facebook catalog feed
          price: formatPriceWithoutCurrency(
            product?.specialOfferPricing?.priceGross?.value ||
              product?.pricing?.priceGross?.value ||
              0
          ), // 1 item price (with applied discount - if exist)
          discount: product.specialOfferPricing
            ? formatPriceWithoutCurrency(
                product?.pricing?.priceGross?.value ||
                  0 - product?.specialOfferPricing?.priceGross?.value ||
                  0
              )
            : 0, // The monetary discount value associated with the 1 item. - if no discount 0
          currency:
            product?.pricing?.priceGross?.symbol === 'zł' ? 'PLN' : undefined, //currency
          item_brand: product?.brand?.name || undefined, // product manufacturer
          item_promotion: product.specialOfferPricing ? 'yes' : 'no', // yes if promotion applied, no if no promotion
          item_available: product.isAvailable ? 'yes' : 'no', // yes if item available, no if not
          item_new: isNewProduct(product.tags || []) ? 'yes' : 'no', // yes if item new (nowość) in shop, no if not new product
          item_adult_only:
            product.ageLimit && product.ageLimit >= 18 ? 'yes' : 'no', // yes if item for clients 18+, no if for everyone
          index: idxOnList, //product position on the list - starts from 0
          ...getProductCategoriesForSeo(product), //Category path corresponding to the one on the page - if there are more steps in path add another item_category2 etc. (MAX number item_category5)
          item_variant: getProductUnitDetails(product) || undefined, //variant of the product
          item_list_name: matchedListName, //list name where product displayed
          quantity: productQuantity || 1, //number of pieces - default value 1
          creative_name:
            storedItem?.promotion?.creativeName ||
            isLastProductSelected?.promotion?.creativeName ||
            promotion?.creativeName,
          promotion_id:
            storedItem?.promotion?.promotionId ||
            isLastProductSelected?.promotion?.promotionId ||
            promotion?.promotionId ||
            undefined
        }
      ]
    }
  }

  queueAnalyticsEvent(eventData)
}

interface beginCheckoutProps {
  user: CustomerTypes | undefined
  products: CartItem[]
  cart?: Cart | null
}

export const beginCheckout = ({ user, products, cart }: beginCheckoutProps) => {
  queueAnalyticsEvent({ ecommerce: null })

  const items: Ga4Item[] = collectItemsByCart(products)
  const eventData = {
    event: SeoEvent.BEGIN_CHECKOUT, //constant
    type: 'ecommerce', //constant
    user_id: user?.code || undefined, //User ID from crm if logged in, if not we return undefined
    user_adult: isCustomerAdult(user?.dateOfBirth), //1 if user have more than 18 years, 0 - if no, if no value we return undefined
    hashed_email: user?.email ? md5(user?.email) : undefined, //customer email in md5 - if logged in, if not we return undefined
    ecommerce: {
      value: formatPriceWithoutCurrency(cart?.paymentValue.value || 0),
      currency: 'PLN',
      coupon: cart?.discountCodes[0]?.code || undefined,
      items
    }
  }

  queueAnalyticsEvent(eventData)
}

interface addShippingInfoProps {
  user: CustomerTypes | undefined
  cart: Cart | null
}

export const addShippingInfo = ({ user, cart }: addShippingInfoProps) => {
  queueAnalyticsEvent({ ecommerce: null })

  const items: Ga4Item[] = collectItemsByCart(cart?.items || [])
  const eventData = {
    event: SeoEvent.ADD_SHIPPING_INFO, //constant
    type: 'ecommerce', //constant
    user_id: user?.code || undefined, //User ID from crm if logged in, if not we return undefined
    user_adult: isCustomerAdult(user?.dateOfBirth), //1 if user have more than 18 years, 0 - if no, if no value we return undefined
    hashed_email: user?.email ? md5(user?.email) : undefined, //customer email in md5 - if logged in, if not we return undefined
    delivery_week_day: getNumberOfDayFromDate(cart?.deliverySlot?.date || ''), //delivery day of the week - 1 it is monday, 2 tuesday, etc.
    delivery_hour: `${cart?.deliverySlot?.start || '??'}-${
      cart?.deliverySlot?.end || '??'
    }`, //delivery hour in 24 hours, starting 00, 01, 02
    ecommerce: {
      value: formatPriceWithoutCurrency(cart?.paymentValue.value || 0),
      currency: 'PLN',
      coupon: cart?.discountCodes[0]?.code || undefined,
      shipping_tier: cart?.deliverySlot?.deliveryMethod?.name || undefined,
      items
    }
  }

  queueAnalyticsEvent(eventData)
}

interface removeToCartProps {
  user: CustomerTypes | undefined
  product: ProductTypes
  button_place?: string
  idxOnList: number
  productQuantity: number
  cart: Cart | null
  listName: SeoListName
  type: 'decrement' | 'remove'
}

export const removeFromCartSeo = ({
  user,
  product,
  button_place,
  idxOnList,
  productQuantity,
  cart,
  listName,
  type
}: removeToCartProps) => {
  if (type !== 'remove') {
    return
  }

  const coupon = cart?.discountCodes[0]?.code || undefined
  const storagedProductSeoData = ProductSeoStorage.find(product.id) || undefined

  queueAnalyticsEvent({ ecommerce: null })
  const eventData = {
    event: SeoEvent.REMOVE_FROM_CART, //constant
    type: 'ecommerce', //constant
    user_id: user?.code || undefined, //User ID from crm if logged in, if not we return undefined
    user_adult: isCustomerAdult(user?.dateOfBirth), //1 if user have more than 18 years, 0 - if no, if no value we return undefined
    hashed_email: user?.email ? md5(user?.email) : undefined, //customer email in md5 - if logged in, if not we return undefined
    button_place: button_place || undefined, //button place where product was added to cart. It can be like: cart page, product page, product page - listing, category page, profile page, etc.
    ecommerce: {
      value: formatPriceWithoutCurrency(
        product?.pricing?.priceGross?.value || 0
      ),
      currency:
        product?.pricing?.priceGross?.symbol === 'zł' ? 'PLN' : undefined,
      coupon, //coupon if exists - if not undefined
      items: [
        {
          item_name: product.nameLong || undefined, //product name
          item_id: product.code || undefined, // product id - it should be the same as you have in google merchant center and facebook catalog feed
          price: formatPriceWithoutCurrency(
            product?.specialOfferPricing?.priceGross?.value ||
              product?.pricing?.priceGross?.value ||
              0
          ), // 1 item price (with applied discount - if exist)
          discount: product.specialOfferPricing
            ? formatPriceWithoutCurrency(
                product?.pricing?.priceGross?.value ||
                  0 - product?.specialOfferPricing?.priceGross?.value ||
                  0
              )
            : 0, // The monetary discount value associated with the 1 item. - if no discount 0
          currency:
            product?.pricing?.priceGross?.symbol === 'zł' ? 'PLN' : undefined, //currency
          item_brand: product?.brand?.name || undefined, // product manufacturer
          item_promotion: product.specialOfferPricing ? 'yes' : 'no', // yes if promotion applied, no if no promotion
          item_available: product.isAvailable ? 'yes' : 'no', // yes if item available, no if not
          item_new: isNewProduct(product.tags || []) ? 'yes' : 'no', // yes if item new (nowość) in shop, no if not new product
          item_adult_only:
            product.ageLimit && product.ageLimit >= 18 ? 'yes' : 'no', // yes if item for clients 18+, no if for everyone
          index: idxOnList || 0, //product position on the list - starts from 0
          ...getProductCategoriesForSeo(product), //Category path corresponding to the one on the page - if there are more steps in path add another item_category2 etc. (MAX number item_category5)
          item_variant: getProductUnitDetails(product) || undefined, //variant of the product
          item_list_name:
            storagedProductSeoData?.listName || listName || undefined, //list name where product displayed
          quantity: type === 'remove' ? productQuantity : 1, //number of pieces - default value 1
          creative_name:
            storagedProductSeoData?.promotion?.creativeName || undefined,
          promotion_id:
            storagedProductSeoData?.promotion?.promotionId || undefined
        }
      ]
    }
  }

  queueAnalyticsEvent(eventData)
  ProductSeoStorage.remove(product.id)
}

interface viewCartSeoProps {
  user: CustomerTypes | undefined
  cart: Cart | null
}

export const viewCartSeo = ({ user, cart }: viewCartSeoProps) => {
  queueAnalyticsEvent({ ecommerce: null })

  const items: Ga4Item[] = collectItemsByCart(cart?.items || [])

  const eventData = {
    event: SeoEvent.VIEW_CART, //constant
    type: 'ecommerce', //constant
    user_id: user?.code || undefined, //User ID from crm if logged in, if not we return undefined
    user_adult: isCustomerAdult(user?.dateOfBirth), //1 if user have more than 18 years, 0 - if no, if no value we return undefined
    hashed_email: user?.email ? md5(user?.email) : undefined, //customer email in md5 - if logged in, if not we return undefined
    ecommerce: {
      value: formatPriceWithoutCurrency(cart?.totalValueGross?.value || 0),
      currency: 'PLN',
      coupon: cart?.discountCodes[0]?.code || undefined,
      items
    }
  }

  queueAnalyticsEvent(eventData)
}

export const syneriseEventCartStatus = async (cart: Cart) => {
  try {
    const eventData = {
      products: cart?.items.map(({ product, quantity }: CartItem) => ({
        sku: product.code,
        quantity: quantity,
        brand: product.brand,
        category: product.category
      })),
      totalAmount: cart?.totalValueGross.value / 100,
      totalQuantity: cart?.items.reduce((acc, item) => acc + item.quantity, 0),
      itemIds: cart?.items.map(({ product }: CartItem) => product.code)
    }

    logAnalyticsEvents({ event: 'cart.status', ...eventData })
    if (window.SR?.event) {
      window.SR.event.trackCustomEvent('cart.status', eventData, 'CartStatus')
    }
  } catch (error) {
    console.error('Error tracking cart status with Synerise:', error)
  }
}
